/**
 * @file PatientAppointments.jsx — /mis-citas
 */
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  Clock,
  MapPin,
  Video,
  ChevronRight,
} from "lucide-react";
import { useAppointments } from "../hooks/useAppointments";
import { PATIENT_STRINGS, APPOINTMENT_STRINGS } from "../constants/strings";
import { ACTIVE_STATUSES } from "../../../shared/appointment-status.js";
import { Avatar, Card, Chip, Spinner, Tabs, TabsList, TabsTrigger, TabsContent } from "../ui";
import "./PatientAppointments.css";

const STATUS_TONES = {
  AGENDADA: "warn",
  CONFIRMADA: "ok",
  COMPLETADA: "primary",
  CANCELADA: "neutral",
  NO_ASISTIO: "err",
};

export default function PatientAppointments() {
  const [tab, setTab] = useState("upcoming");
  const { appointments, isLoading } = useAppointments();
  const navigate = useNavigate();

  const now = new Date();

  const upcoming = useMemo(
    () =>
      (appointments || [])
        .filter((a) => new Date(a.dateTime) >= now && ACTIVE_STATUSES.includes(a.status))
        .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime)),
    [appointments]
  );

  const history = useMemo(
    () =>
      (appointments || [])
        .filter((a) => !ACTIVE_STATUSES.includes(a.status) || new Date(a.dateTime) < now)
        .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime)),
    [appointments]
  );

  if (isLoading) {
    return (
      <div className="pa-page">
        <div className="pa-loading">
          <Spinner size={28} />
        </div>
      </div>
    );
  }

  const renderCard = (appt) => {
    const pro = appt.professional;
    const proName = pro
      ? [pro.title, pro.user?.firstName, pro.user?.lastName].filter(Boolean).join(" ")
      : "";
    const initials = pro
      ? (pro.user?.firstName?.[0] || "") + (pro.user?.lastName?.[0] || "")
      : "";
    const dt = new Date(appt.dateTime);
    const location = appt.locationId ? pro?.locations?.find((l) => l.id === appt.locationId) : null;
    const specialty = pro?.specialties?.[0]?.name;
    return (
      <Card key={appt.id} hover className="pa-card">
        <button type="button" className="pa-card-link" onClick={() => navigate(`/cita/${appt.id}`)}>
          <div className="pa-card-row">
            <Chip tone={STATUS_TONES[appt.status] || "neutral"} size="sm">
              {APPOINTMENT_STRINGS.STATUS_LABELS[appt.status] || appt.status}
            </Chip>
            <ChevronRight size={16} className="pa-card-arrow" />
          </div>
          <div className="pa-card-pro">
            <Avatar size="sm" initials={initials.toUpperCase()} alt={proName} />
            <div className="pa-card-pro-text">
              <span className="pa-card-pro-name">{proName}</span>
              {specialty && <span className="pa-card-pro-specialty">{specialty}</span>}
            </div>
          </div>
          <div className="pa-card-meta">
            <span className="pa-card-meta-row">
              <Clock size={12} />
              {dt.toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" })},{" "}
              {dt.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
            </span>
            {location && (
              <span className="pa-card-meta-row">
                {location.isVirtual ? <Video size={12} /> : <MapPin size={12} />}
                {location.isVirtual
                  ? location.name || "Teleconsulta"
                  : [location.address, location.colonia, location.city, location.state]
                      .filter(Boolean)
                      .join(", ")}
              </span>
            )}
            {appt.reason && <span className="pa-card-reason">{appt.reason}</span>}
          </div>
        </button>
      </Card>
    );
  };

  return (
    <div className="pa-page">
      <header className="pa-head">
        <span className="pa-eyebrow">Mis citas</span>
        <h1 className="pa-title">{PATIENT_STRINGS.TAB_APPOINTMENTS}</h1>
      </header>

      <Tabs value={tab} onValueChange={setTab} variant="pill">
        <TabsList>
          <TabsTrigger value="upcoming">
            {PATIENT_STRINGS.TAB_UPCOMING} ({upcoming.length})
          </TabsTrigger>
          <TabsTrigger value="history">
            {PATIENT_STRINGS.TAB_HISTORY} ({history.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming">
          {upcoming.length === 0 ? (
            <Card className="pa-empty">
              <CalendarDays size={36} className="pa-empty-icon" />
              <p className="pa-empty-text">{PATIENT_STRINGS.NO_UPCOMING_APPOINTMENTS}</p>
            </Card>
          ) : (
            <div className="pa-list">{upcoming.map(renderCard)}</div>
          )}
        </TabsContent>
        <TabsContent value="history">
          {history.length === 0 ? (
            <Card className="pa-empty">
              <CalendarDays size={36} className="pa-empty-icon" />
              <p className="pa-empty-text">{PATIENT_STRINGS.NO_HISTORY}</p>
            </Card>
          ) : (
            <div className="pa-list">{history.map(renderCard)}</div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
